import { getCompletedPayoutDisplay } from './collaborationPayoutDisplay';
import { isJourneyItemComplete } from './collaborationJourneyStats';

const HEALTH_RANK = { at_risk: 0, needs_attention: 1, healthy: 2 };

export function compareOperationsByUrgency(a, b) {
  const aDays = a.days_waiting || 0;
  const bDays = b.days_waiting || 0;
  if (aDays !== bDays) return bDays - aDays;
  return (HEALTH_RANK[a.health] ?? 3) - (HEALTH_RANK[b.health] ?? 3);
}

function comparePartitionByUrgency(a, b) {
  if (a.days_waiting !== b.days_waiting) return b.days_waiting - a.days_waiting;
  if (a.health !== b.health) {
    return HEALTH_RANK[a.health] - HEALTH_RANK[b.health];
  }
  return new Date(a.created_at) - new Date(b.created_at);
}

export function partitionOperationsItems(items) {
  const urgentItems = [];
  const actionNeededItems = [];
  const waitingItems = [];
  const completedItems = [];

  items.forEach((row) => {
    if (isJourneyItemComplete(row)) {
      completedItems.push(row);
      return;
    }
    // Waiting ownership beats urgency so KPI counts match the Waiting filter list.
    if (row.journey_waiting) {
      waitingItems.push(row);
      return;
    }
    if (row.is_urgent) {
      urgentItems.push(row);
      return;
    }
    actionNeededItems.push(row);
  });

  urgentItems.sort(comparePartitionByUrgency);
  actionNeededItems.sort(comparePartitionByUrgency);
  waitingItems.sort(comparePartitionByUrgency);
  completedItems.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return {
    urgentItems,
    actionNeededItems,
    waitingItems,
    completedItems,
  };
}

function partyFieldValue(item, partyFields) {
  for (let i = 0; i < partyFields.length; i += 1) {
    const value = item[partyFields[i]];
    if (value) return value;
  }
  return '';
}

export function filterOperationsList(items, {
  query = '',
  healthFilter = 'all',
  stageFilter = 'all',
  partyFields = [],
} = {}) {
  const normalizedQuery = query.trim().toLowerCase();
  return items.filter((item) => {
    if (healthFilter !== 'all' && item.health !== healthFilter) return false;
    if (stageFilter !== 'all' && item.workflow_stage !== stageFilter) return false;
    if (!normalizedQuery) return true;

    const campaign = (item.campaign_name || item.product_name || '').toLowerCase();
    const party = partyFieldValue(item, partyFields).toLowerCase();
    const action = (item.next_action || '').toLowerCase();
    return campaign.includes(normalizedQuery)
      || party.includes(normalizedQuery)
      || action.includes(normalizedQuery);
  });
}

export function sortOperationsList(items, sortBy = 'urgency', {
  partySortKey = 'creator',
  partyFields = ['influencer_name'],
} = {}) {
  const sorted = [...items];
  if (sortBy === 'campaign') {
    sorted.sort((a, b) => (
      (a.campaign_name || a.product_name || '').localeCompare(b.campaign_name || b.product_name || '')
    ));
    return sorted;
  }
  if (sortBy === partySortKey) {
    sorted.sort((a, b) => (
      partyFieldValue(a, partyFields).localeCompare(partyFieldValue(b, partyFields))
    ));
    return sorted;
  }
  if (sortBy === 'days') {
    sorted.sort((a, b) => b.days_waiting - a.days_waiting);
    return sorted;
  }
  sorted.sort((a, b) => {
    if (a.is_urgent !== b.is_urgent) return a.is_urgent ? -1 : 1;
    if (a.days_waiting !== b.days_waiting) return b.days_waiting - a.days_waiting;
    return new Date(a.created_at) - new Date(b.created_at);
  });
  return sorted;
}

export function pickFilteredOperationPartitions(filteredItems, partitions) {
  const filteredIds = new Set(filteredItems.map((item) => item.id));
  const pick = (items) => items.filter((item) => filteredIds.has(item.id));
  return {
    urgentItems: pick(partitions.urgentItems),
    actionNeededItems: pick(partitions.actionNeededItems),
    waitingItems: pick(partitions.waitingItems),
    completedItems: pick(partitions.completedItems),
  };
}

export function enrichOperationsItem(journeyItem, stepSource, {
  deriveHealth,
  getDaysWaiting,
  deriveWorkflowStage,
  getCampaignThumbnailUrl,
  getPartyAvatar,
  partyAvatarKey,
  payoutPerspective,
}) {
  const daysWaiting = getDaysWaiting(journeyItem, stepSource);
  const workflowStage = deriveWorkflowStage(journeyItem.journey_label);
  const health = deriveHealth(journeyItem, daysWaiting);
  const isComplete = isJourneyItemComplete(journeyItem);
  const isUrgent = health === 'at_risk'
    || (!journeyItem.journey_waiting && daysWaiting >= 3 && !isComplete);

  return {
    ...journeyItem,
    workflow_stage: workflowStage,
    next_action: journeyItem.journey_label,
    days_waiting: daysWaiting,
    health,
    is_urgent: isUrgent,
    campaign_thumbnail: getCampaignThumbnailUrl(stepSource, journeyItem),
    [partyAvatarKey]: getPartyAvatar(stepSource, journeyItem),
    payout_display: isComplete ? getCompletedPayoutDisplay(stepSource, payoutPerspective) : null,
  };
}
